import { useState, useEffect } from "react";

import { dateUtils } from "/imports/libs/url";
import api from "/imports/api/client";
import { track } from "/imports/libs/analytics";
import { ALL_DURATIONS } from "/imports/ui/components/booking";

/**
 * The lessons page's times: one day (mobile, desktop Day) or the seven visible days
 * (desktop Week), for the current filters — or, in reschedule mode, the times the lesson
 * being moved can go to. Refetches whenever a filter, the view or `reloadKey` changes.
 */
export default function useLessonAvailability({
  slug,
  filters,
  selectedLocation,
  isMobile,
  viewMode,
  weekDates,
  pack,
  reschedule,
  reloadKey,
  lessonServices = []
}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [availability, setAvailability] = useState(null);
  // Pack offers for the loaded slots, keyed by their offerKey. Carried by the
  // availability payload so they land in the same tick as the times.
  const [packageOffers, setPackageOffers] = useState(null);
  const [weekSlotsByDate, setWeekSlotsByDate] = useState(null); // Map<dateISO, slots[]>

  useEffect(() => {
    // Duration is required: with no service or no duration chosen there is
    // nothing to fetch — the view shows the "choose a duration" prompt instead.
    // A lesson being moved already names both.
    if (!slug || (!reschedule.active && (!filters.serviceId || !filters.duration))) {
      setAvailability(null);
      setPackageOffers(null);
      setWeekSlotsByDate(null);
      setLoading(false);
      return undefined;
    }
    let cancelled = false;

    (async () => {
      setLoading(true);
      setError(null);
      try {
        const baseParams = {
          serviceId: filters.serviceId,
          trainerId: filters.trainerId,
          // "All durations": no length on the request — the server probes every
          // length the service sells and the groups carry one slot per length.
          duration: filters.duration && filters.duration !== ALL_DURATIONS ? filters.duration : undefined,
          locationId: filters.locationId || selectedLocation?._id,
          // Pack mode past the first pick: opens the days the pack is valid for
          // but the public booking window isn't.
          ...(pack.packAvailabilityParams || {})
        };

        // Pack mode: no pack-offer nudges — the client is already buying one.
        const withOffers = !pack.active;

        // Searches that find nothing are demand the club can't serve — the
        // slot count is the point of this event. A move is not a search for
        // something new, so it stays out of the figures.
        const trackSearch = (days, slotsFound) => {
          if (reschedule.active) return;
          track("availability_searched", {
            kind: "lesson",
            service_id: baseParams.serviceId,
            service_name: lessonServices.find((s) => s._id === baseParams.serviceId)?.name || null,
            location_id: baseParams.locationId || null,
            trainer_id: baseParams.trainerId || null,
            duration: baseParams.duration || "all",
            date: filters.date,
            days_searched: days,
            slots_found: slotsFound,
            pack_mode: !!pack.active
          });
        };

        // Reschedule mode asks for the times THIS lesson can move to; the server pins the
        // service and duration itself.
        const loadDay = (date) =>
          reschedule.active
            ? api.getLessonRescheduleSlots(slug, reschedule.bookingId, { date, trainerId: filters.trainerId, locationId: filters.locationId })
            : api.getLessonAvailability(slug, { date, ...baseParams, withOffers });

        if (isMobile || viewMode === "day") {
          // Mobile, and desktop Day mode, both show a single selected day.
          const data = await loadDay(filters.date);
          if (!cancelled) {
            setAvailability(data);
            setPackageOffers(data?.packageOffers || null);
            setWeekSlotsByDate(null);
            if (data?.reschedule) reschedule.setInfo(data.reschedule);
            trackSearch(1, data?.slots?.length || 0);
          }
          return;
        }

        // Desktop week grid: fetch the 7 visible days in parallel. Past days
        // can't be booked, so we skip the network call and treat them as empty.
        const todayISOv = dateUtils.today();
        const datesToFetch = weekDates.filter((d) => d >= todayISOv);
        // A lesson being moved asks for the whole week at once, so the server checks it once.
        const loadRescheduleWeek = async () => {
          if (!datesToFetch.length) return [];
          const week = await api.getLessonRescheduleWeek(slug, reschedule.bookingId, {
            from: datesToFetch[0],
            days: datesToFetch.length,
            trainerId: filters.trainerId,
            locationId: filters.locationId
          });
          return datesToFetch.map((d, i) => (i === 0 ? week : { slots: week.days?.[d] || [] }));
        };
        const results = reschedule.active ? await loadRescheduleWeek() : await Promise.all(datesToFetch.map((d) => loadDay(d)));
        if (cancelled) return;
        trackSearch(
          datesToFetch.length,
          results.reduce((n, r) => n + (r?.slots?.length || 0), 0)
        );
        const rescheduleInfo = results.find((r) => r?.reschedule)?.reschedule;
        if (rescheduleInfo) reschedule.setInfo(rescheduleInfo);
        const byDate = new Map(weekDates.map((d) => [d, []]));
        datesToFetch.forEach((d, i) => byDate.set(d, results[i]?.slots || []));
        setWeekSlotsByDate(byDate);
        // Trainers/durations are date-independent — take them from the first
        // day that returned a payload to drive the sidebar filters.
        setAvailability(results.find(Boolean) || null);
        // Every day answers the pack question for the branches and tiers it
        // happens to hold, so the week's answers merge into one map — a day
        // with nothing on must not be the one that decides the hint.
        setPackageOffers(Object.assign({}, ...results.map((r) => r?.packageOffers || {})));
      } catch (err) {
        if (cancelled || (reschedule.active && reschedule.leaveIfChanged(err))) return;
        setError(err.message || "Failed to load availability");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    slug,
    filters.date,
    filters.serviceId,
    filters.trainerId,
    filters.duration,
    filters.locationId,
    selectedLocation?._id,
    isMobile,
    viewMode,
    weekDates,
    pack.active,
    // Both halves matter: naming the purchase (so owned credit is ignored here)
    // and the first pick (which is what widens the days).
    pack.packAvailabilityParams?.packProduct,
    pack.packAvailabilityParams?.packFirstDate,
    // A lesson the client just cancelled from the tray frees its time.
    pack.slotsVersion,
    reschedule.bookingId,
    reloadKey
  ]);

  return { loading, error, availability, packageOffers, weekSlotsByDate };
}
